import React from 'react';
import { makeStyles } from 'design-system/styles';
import { Line } from './namedEntityRecognitionParser';
import classNames from 'classnames';
import { NER_CANVAS_MAX_WIDTH } from './NamedEntityRecognition';

const LINE_HEIGHT = 100;

function parseTextIntoLines(text: string, mockTextEl: HTMLDivElement | null): Line[] {
  const lines: Line[] = [];
  if (!mockTextEl) {
    return lines;
  }

  let currentLineIdx = 0;
  let currentMockElIdx = 0;
  let currentMockCharIdx = 0;
  let currentLine: Line = {
    value: '',
    start: 0,
    end: -1,
  };

  const range = document.createRange();

  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\n') {
      currentLine.end = i;
      lines.push(currentLine);
      currentLineIdx++;
      currentMockElIdx++;
      currentMockCharIdx = 0;
      currentLine = {
        value: '',
        start: i + 1,
        end: -1,
      };
      continue;
    }

    const mockTextNode = mockTextEl.childNodes[currentMockElIdx].childNodes[0] as Text;

    range.setStart(mockTextNode, currentMockCharIdx);
    range.setEnd(mockTextNode, currentMockCharIdx + 1);
    const lineIdx = Math.floor(range.getBoundingClientRect().y / LINE_HEIGHT);

    if (lineIdx !== currentLineIdx) {
      currentLine.end = i;
      lines.push(currentLine);
      currentLineIdx++;
      currentMockCharIdx++;
      currentLine = {
        value: text[i],
        start: i,
        end: -1,
      };
    } else {
      currentLine.value += text[i];
      currentMockCharIdx++;
    }
  }

  currentLine.end = text.length;
  lines.push(currentLine);

  range.detach(); // GC - unclear if this is relevant in 2021

  return lines;
}

interface NERLineParserProps {
  text: string;
  textClassName: string;
  maxWidth: number | null;
  mergeNewlines?: boolean;
  onLinesChange: (lines: Line[]) => void;
}

const NERLineParser: React.FC<NERLineParserProps> = ({
  text,
  textClassName,
  maxWidth,
  mergeNewlines,
  onLinesChange,
}) => {
  const effectiveText = mergeNewlines ? text.replaceAll('\n', ' ') : text;
  const linesRef = React.useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    if (!linesRef.current) {
      return;
    }
    onLinesChange(parseTextIntoLines(effectiveText, linesRef.current));
  }, [onLinesChange, effectiveText, linesRef, maxWidth]);

  const classes = useNERLineParserStyles();
  return (
    <div
      className={classNames(classes.parser, textClassName)}
      style={{ maxWidth: maxWidth ?? NER_CANVAS_MAX_WIDTH }}
      ref={linesRef}
    >
      {effectiveText.split('\n').map((fragment, idx) => (
        <div key={idx}>{fragment.trim() ? fragment : `placeholder:${fragment}`}</div>
      ))}
    </div>
  );
};

const useNERLineParserStyles = makeStyles({
  parser: {
    paddingLeft: 30,
    paddingRight: 30,
    position: 'fixed',
    visibility: 'hidden',
    top: 0,
    lineHeight: `${LINE_HEIGHT}px`,
    boxSizing: 'border-box',
  },
});

export default NERLineParser;
